import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const L = {
  fr: { star: "Envoyer une étoile", stars: "étoiles pour cet épisode", ask: "Pose ta question à l'antenne", name: "Ton prénom", q: "Ta question ou réflexion…", send: "Envoyer", feed: "Sur l'antenne", empty: "Sois le premier à lancer une question.", refused: "Ce message ne peut pas être affiché sur l'antenne.", short: "Écris ta question en quelques mots.", slow: "Doucement… attends quelques secondes.", err: "Oups, réessaie dans un instant.", now: "à l'instant", min: "min" },
  en: { star: "Send a star", stars: "stars for this episode", ask: "Ask your question on air", name: "Your first name", q: "Your question or thought…", send: "Send", feed: "On air", empty: "Be the first to ask a question.", refused: "This message can't be shown on air.", short: "Write your question in a few words.", slow: "Easy… wait a few seconds.", err: "Oops, try again in a moment.", now: "just now", min: "min" },
  es: { star: "Enviar una estrella", stars: "estrellas para este episodio", ask: "Haz tu pregunta en antena", name: "Tu nombre", q: "Tu pregunta o reflexión…", send: "Enviar", feed: "En antena", empty: "Sé el primero en lanzar una pregunta.", refused: "Este mensaje no puede mostrarse en antena.", short: "Escribe tu pregunta en pocas palabras.", slow: "Despacio… espera unos segundos.", err: "Vaya, inténtalo de nuevo.", now: "ahora", min: "min" },
  nl: { star: "Stuur een ster", stars: "sterren voor deze aflevering", ask: "Stel je vraag in de uitzending", name: "Je voornaam", q: "Je vraag of gedachte…", send: "Versturen", feed: "In de uitzending", empty: "Stel als eerste een vraag.", refused: "Dit bericht kan niet in de uitzending worden getoond.", short: "Schrijf je vraag in een paar woorden.", slow: "Rustig… wacht een paar seconden.", err: "Oeps, probeer het zo opnieuw.", now: "zojuist", min: "min" },
};
const INPUT = "rounded-xl bg-background/60 border border-white/10 px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/35 focus:border-primary/50 focus:outline-none";

const ago = (iso, T) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? T.now : `${m} ${T.min}`;
};

export const RadioReactions = ({ channel, slug, lang }) => {
  const T = L[lang] || L.fr;
  const [data, setData] = useState({ questions: [], stars: 0 });
  const [bursts, setBursts] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("coccon_radio_name") || "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/radio/reactions", { params: { channel, slug } }).then(({ data: d }) => { if (alive) setData(d); }).catch(() => {});
    load();
    const id = setInterval(load, 6000);
    return () => { alive = false; clearInterval(id); };
  }, [channel, slug]);

  const errMsg = (e) => ({ 422: T.refused, 400: T.short, 429: T.slow }[e?.response?.status] || T.err);

  const star = async () => {
    const id = Date.now();
    setBursts((b) => [...b, id]);
    setTimeout(() => setBursts((b) => b.filter((x) => x !== id)), 1400);
    try {
      const { data: d } = await api.post("/radio/react", { kind: "star", channel, slug });
      setData((s) => ({ ...s, stars: d.stars }));
    } catch (e) { if (e?.response?.status !== 429) toast.error(errMsg(e)); }
  };

  const ask = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (text.trim().length < 3) { toast.error(T.short); return; }
    setBusy(true);
    localStorage.setItem("coccon_radio_name", name.trim());
    try {
      await api.post("/radio/react", { kind: "question", channel, slug, name: name.trim(), text: text.trim() });
      setText("");
      const { data: d } = await api.get("/radio/reactions", { params: { channel, slug } });
      setData(d);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 glass rounded-3xl border border-white/10 p-5 sm:p-6" data-testid="radio-reactions">
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <div className="relative">
          <button onClick={star} data-testid="radio-star-btn" aria-label={T.star}
            className="h-12 w-12 rounded-full border border-primary/40 bg-primary/10 text-primary inline-flex items-center justify-center hover:bg-primary/20 hover:scale-105 active:scale-95 transition-transform">
            <Star size={20} fill="currentColor" />
          </button>
          <AnimatePresence>
            {bursts.map((id) => (
              <motion.span key={id} initial={{ opacity: 1, y: 0, scale: 0.6 }} animate={{ opacity: 0, y: -70, scale: 1.4 }} exit={{ opacity: 0 }} transition={{ duration: 1.3, ease: "easeOut" }}
                className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 text-amber-300"><Star size={18} fill="currentColor" /></motion.span>
            ))}
          </AnimatePresence>
        </div>
        <p className="text-sm text-foreground/60" data-testid="radio-star-count">
          <span className="font-semibold text-foreground/90 tabular-nums">{data.stars}</span> {T.stars}
        </p>
      </div>

      <form onSubmit={ask} className="grid sm:grid-cols-[140px_1fr_auto] gap-2 mb-5" data-testid="radio-question-form">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={T.name} maxLength={30} className={INPUT} data-testid="radio-question-name" />
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={T.q} maxLength={240} className={INPUT} data-testid="radio-question-text" />
        <button type="submit" disabled={busy} data-testid="radio-question-send"
          className="btn-frost-orange inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {T.send}
        </button>
      </form>

      <p className="text-[0.65rem] uppercase tracking-[0.22em] text-foreground/45 mb-3 flex items-center gap-1.5"><MessageCircle size={12} /> {T.feed}</p>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="radio-question-feed">
        {data.questions.length === 0 && <p className="text-sm text-foreground/40 italic">{T.empty}</p>}
        <AnimatePresence initial={false}>
          {data.questions.map((q) => (
            <motion.div key={q.id} layout initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
              className="rounded-2xl bg-white/[0.04] border border-white/5 px-4 py-2.5" data-testid={`radio-question-${q.id}`}>
              <p className="text-sm text-foreground/90">{q.text}</p>
              <p className="text-[0.7rem] text-foreground/45 mt-0.5"><span className="text-primary/80">{q.name}</span> · {ago(q.created_at, T)}</p>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
